import { createContext } from "react";
import { Breakpoint } from "@mui/material";
import { AuthTempInfo, AuthUser } from "@/types/Auth.ts";
import { AppMenuitem } from "@/types/Menu.ts";
import { AppMenus, AppModules, ModuleInfo } from "@/types/Module.ts";
import { ModuleUtils } from "@/utils/ModuleUtils.ts";
import { AdminRole, RolePermission } from "@/types/AppRole.ts";
import { AppUtils } from "@/utils/AppUtils.ts";
import { ControlPanelUser, UserState } from "@/types/ControlPanel";

export type AppDevice = "mobile" | "tablet" | "pc";

export interface AppContextState {
  auth: {
    user: AuthUser | null;
    tempInfo?: AuthTempInfo;
    roles: AdminRole[];
    permissions: RolePermission[];
  };
  controlPanelUser: ControlPanelUser;
  layout: {
    breakpoint: Breakpoint;
    device: "mobile" | "tablet" | "pc";
  };
  menu: {
    open: boolean;
    items: Array<AppMenuitem>;
  };
  loaded: boolean;
  setAuthUser: (value: AuthUser) => void;
  setControlPanelUser: (value: ControlPanelUser) => void;
  setAuthTempInfo: (value: AuthTempInfo) => void;
  setUserRoles: (value: AdminRole[]) => void;
  setLoaded: (value: boolean) => void;
  setMenuOpen: (value: boolean) => void;
  cleanState: () => void;
}

export const INITIAL_STATE: AppContextState = {
  auth: {
    user: null,
    roles: new Array<AdminRole>(),
    permissions: new Array<RolePermission>(),
  },
  controlPanelUser: {
    state: UserState.INACTIVE,
  },
  layout: {
    breakpoint: "lg",
    device: "pc",
  },
  menu: {
    open: true,
    items: [
      {
        id: AppModules.SETTINGS,
        ...AppUtils.pickObject<ModuleInfo, "label" | "path">(ModuleUtils.getModuleInfo(AppModules.SETTINGS), [
          "label",
          "path",
        ]),
        iconClass: "fa-solid fa-gear",
        items: [
          {
            id: AppModules.SETTINGS,
            ...AppUtils.pickObject<ModuleInfo, "label" | "path">(ModuleUtils.getModuleInfo(AppModules.CONFIGURATION), [
              "label",
              "path",
            ]),
            iconClass: "fa-solid fa-gear",
          },
          {
            id: AppModules.USERS,
            ...AppUtils.pickObject<ModuleInfo, "label" | "path">(ModuleUtils.getModuleInfo(AppModules.USERS), [
              "label",
              "path",
            ]),
            iconClass: "fa-solid fa-users",
          },
          {
            id: AppModules.ROLES,
            ...AppUtils.pickObject<ModuleInfo, "label" | "path">(ModuleUtils.getModuleInfo(AppModules.ROLES), [
              "label",
              "path",
            ]),
            iconClass: "fa-solid fa-lock",
          },
          {
            id: AppModules.DIGITAL_SIGNATURE,
            ...AppUtils.pickObject<ModuleInfo, "label" | "path">(
              ModuleUtils.getModuleInfo(AppModules.DIGITAL_SIGNATURE),
              ["label", "path"]
            ),
            iconClass: "fa-solid fa-pen-fancy",
          },
        ],
      },
      {
        id: AppModules.CONTROL_PANEL,
        ...AppUtils.pickObject<ModuleInfo, "label" | "path">(ModuleUtils.getModuleInfo(AppModules.CONTROL_PANEL), [
          "label",
          "path",
        ]),
        iconClass: "fa-solid fa-network-wired",
        items: [
          {
            id: AppModules.ACCESO_SOPORTE,
            ...AppUtils.pickObject<ModuleInfo, "label" | "path">(ModuleUtils.getModuleInfo(AppModules.ACCESO_SOPORTE), [
              "label",
              "path",
            ]),
            iconClass: "fa-solid fa-person-chalkboard",
          },
        ],
      },
      {
        id: AppMenus.HELPIA_PEOPLE,
        ...AppUtils.pickObject<ModuleInfo, "label" | "path">(ModuleUtils.getModuleInfo(AppModules.HELPIA_PEOPLE), [
          "label",
          "path",
        ]),
        iconClass: "fa-solid fa-users-gear",
        items: [
          {
            id: AppMenus.HELPIA_PEOPLE_AGENT,
            ...AppUtils.pickObject<ModuleInfo, "label" | "path">(
              ModuleUtils.getModuleInfo(AppModules.HELPIA_PEOPLE_AGENT),
              ["label", "path"]
            ),
            iconClass: "fa-solid fa-user-secret",
          },
          {
            id: AppMenus.HELPIA_PEOPLE_PROJECT,
            ...AppUtils.pickObject<ModuleInfo, "label" | "path">(
              ModuleUtils.getModuleInfo(AppModules.HELPIA_PEOPLE_PROJECT),
              ["label", "path"]
            ),
            iconClass: "fa-solid fa-window-restore",
          },
          {
            id: AppMenus.HELPIA_PEOPLE_BRAND,
            ...AppUtils.pickObject<ModuleInfo, "label" | "path">(
              ModuleUtils.getModuleInfo(AppModules.HELPIA_PEOPLE_BRAND),
              ["label", "path"]
            ),
            iconClass: "fa-solid fa-barcode",
          },
          {
            id: AppMenus.HELPIA_PEOPLE_REPORTS,
            ...AppUtils.pickObject<ModuleInfo, "label" | "path">(
              ModuleUtils.getModuleInfo(AppModules.HELPIA_PEOPLE_REPORTS),
              ["label", "path"]
            ),
            iconClass: "fa-solid fa-window-maximize",
          },
        ],
      },
      {
        id: AppMenus.PANEL_EMPRESA,
        ...AppUtils.pickObject<ModuleInfo, "label" | "path">(ModuleUtils.getModuleInfo(AppModules.PANEL_EMPRESA), [
          "label",
          "path",
        ]),
        iconClass: "fa-solid fa-laptop",
        items: [
          {
            id: AppModules.COMPANIES,
            ...AppUtils.pickObject<ModuleInfo, "label" | "path">(ModuleUtils.getModuleInfo(AppModules.COMPANIES), [
              "label",
              "path",
            ]),
            iconClass: "fa-solid fa-building",
          },
          {
            id: AppModules.CHANNELS,
            ...AppUtils.pickObject<ModuleInfo, "label" | "path">(ModuleUtils.getModuleInfo(AppModules.CHANNELS), [
              "label",
              "path",
            ]),
            iconClass: "fa-solid fa-arrow-right-arrow-left",
          },
          {
            id: AppModules.CCENTER,
            ...AppUtils.pickObject<ModuleInfo, "label" | "path">(ModuleUtils.getModuleInfo(AppModules.CCENTER), [
              "label",
              "path",
            ]),
            iconClass: "fa-solid fa-comments",
          },
          {
            id: AppModules.REVIEWS,
            ...AppUtils.pickObject<ModuleInfo, "label" | "path">(ModuleUtils.getModuleInfo(AppModules.REVIEWS), [
              "label",
              "path",
            ]),
            iconClass: "fa-solid fa-bar-chart",
          },
          {
            id: AppModules.INVOICING,
            ...AppUtils.pickObject<ModuleInfo, "label" | "path">(
              ModuleUtils.getModuleInfo(AppModules.INVOICING),
              ["label", "path"]
            ),
            iconClass: "fa-solid fa-file-invoice",
            subitems: [
              {
                id: AppModules.INVOICES,
                ...AppUtils.pickObject<ModuleInfo, "label" | "path">(
                  ModuleUtils.getModuleInfo(AppModules.INVOICES),
                  ["label", "path"]
                ),
                iconClass: "fa-solid fa-hand-holding-dollar",
              }
            ]
          },
          {
            id: AppModules.SUPPORTS_TEMPLATE,
            ...AppUtils.pickObject<ModuleInfo, "label" | "path">(
              ModuleUtils.getModuleInfo(AppModules.SUPPORTS_TEMPLATE),
              ["label", "path"]
            ),
            iconClass: "fa-solid fa-gopuram",
          },
          // {
          //   id: AppModules.CLAIMS,
          //   ...AppUtils.pickObject<ModuleInfo, "label" | "path">(
          //     ModuleUtils.getModuleInfo(AppModules.CLAIMS),
          //     ["label", "path"]
          //   ),
          //   iconClass: "fa-solid fa-bullhorn",
          // },
          {
            id: AppModules.HELP,
            ...AppUtils.pickObject<ModuleInfo, "label" | "path">(ModuleUtils.getModuleInfo(AppModules.HELP), [
              "label",
              "path",
            ]),
            iconClass: "fa-solid fa-circle-question",
          },
        ],
      },
      {
        id: AppMenus.COMERCIAL,
        ...AppUtils.pickObject<ModuleInfo, "label" | "path">(ModuleUtils.getModuleInfo(AppModules.COMERCIAL), [
          "label",
          "path",
        ]),
        iconClass: "fa-solid fa-shop",
        items: [
          {
            id: AppModules.STARTING,
            ...AppUtils.pickObject<ModuleInfo, "label" | "path">(ModuleUtils.getModuleInfo(AppModules.STARTING), [
              "label",
              "path",
            ]),
            iconClass: "fa-solid fa-sign-in",
          },
          {
            id: AppModules.QUOTER,
            ...AppUtils.pickObject<ModuleInfo, "label" | "path">(ModuleUtils.getModuleInfo(AppModules.QUOTER), [
              "label",
              "path",
            ]),
            iconClass: "fa-solid fa-lock",
          },
          {
            id: AppModules.PLANS,
            ...AppUtils.pickObject<ModuleInfo, "label" | "path">(ModuleUtils.getModuleInfo(AppModules.PLANS), [
              "label",
              "path",
            ]),
            iconClass: "fa-solid fa-credit-card",
          },
          {
            id: AppModules.BLOG,
            ...AppUtils.pickObject<ModuleInfo, "label" | "path">(ModuleUtils.getModuleInfo(AppModules.BLOG), [
              "label",
              "path",
            ]),
            iconClass: "fa-solid fa-blog",
          },
          {
            id: AppModules.FAQS,
            ...AppUtils.pickObject<ModuleInfo, "label" | "path">(ModuleUtils.getModuleInfo(AppModules.FAQS), [
              "label",
              "path",
            ]),
            iconClass: "fa-solid fa-question",
          },
        ],
      },
      {
        id: AppMenus.HISTORICO,
        ...AppUtils.pickObject<ModuleInfo, "label" | "path">(ModuleUtils.getModuleInfo(AppModules.HISTORICO), [
          "label",
          "path",
        ]),
        iconClass: "fa-solid fa-trowel-bricks",
        items: [
          {
            id: AppModules.LOGS,
            ...AppUtils.pickObject<ModuleInfo, "label" | "path">(ModuleUtils.getModuleInfo(AppModules.LOGS), [
              "label",
              "path",
            ]),
            iconClass: "fa-solid fa-boxes-stacked",
          },
        ],
      },
      {
        id: AppMenus.GENERAL_REPORTS,
        ...AppUtils.pickObject<ModuleInfo, "label" | "path">(ModuleUtils.getModuleInfo(AppModules.GENERAL_REPORTS), [
          "label",
          "path",
        ]),
        iconClass: "fa-solid fa-sheet-plastic",
        items: [
          {
            id: AppModules.RESUMEN_ENCUESTA,
            ...AppUtils.pickObject<ModuleInfo, "label" | "path">(
              ModuleUtils.getModuleInfo(AppModules.RESUMEN_ENCUESTA),
              ["label", "path"]
            ),
            iconClass: "fa-solid fa-pencil",
          },
          {
            id: AppModules.DYNAMIC_REPORT,
            ...AppUtils.pickObject<ModuleInfo, "label" | "path">(ModuleUtils.getModuleInfo(AppModules.DYNAMIC_REPORT), [
              "label",
              "path",
            ]),
            iconClass: "fa-solid fa-gear",
          },
        ],
      },
    ],
  },
  loaded: false,
} as AppContextState;
export const AppContext = createContext(INITIAL_STATE);
